import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { KeyManagementService } from '@/services/KeyManagementService';
import { EncryptionService } from '@/services/encryption/EncryptionService';
import { routeStore } from '@/store/configuration';
import { IGlobalSupportUserKeys, IPairKey } from '@/store/models/encryption.model';
import { TCryptoAction, EncryptionActionTypes, ICryptoState } from '@/store/types/encryption.types';
import { TAppError } from '@/store/types/base.state';

export const fetchGlobalSupportPublicKey = (): ThunkAction<Promise<void>, ICryptoState, {}, TCryptoAction> => {
  const request = (): TCryptoAction => ({
    type: EncryptionActionTypes.FETCH_GLOBAL_SUPPORT_PUBLIC_KEY,
  });
  const fail = (error: TAppError): TCryptoAction => ({
    type: EncryptionActionTypes.FETCH_GLOBAL_SUPPORT_PUBLIC_KEY_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/api-basics/encryption.html#working-with-keys',
  });
  const success = (globalSupportPublicKey: string): TCryptoAction => ({
    type: EncryptionActionTypes.FETCH_GLOBAL_SUPPORT_PUBLIC_KEY_SUCCESS,
    shouldSkipToaster: true,
    globalSupportPublicKey,
  });

  return async (dispatch: ThunkDispatch<ICryptoState, {}, TCryptoAction>): Promise<void> => {
    dispatch(request());

    try {
      const { globalSupportPublicKey } = await KeyManagementService.fetchGlobalSupportPublicKey();

      dispatch(success(globalSupportPublicKey));
    } catch (error) {
      console.error(error);
      dispatch(fail(error));
    }
  };
};

export const setGlobalSupportPublicKey = (
  globalSupportUser: IGlobalSupportUserKeys,
): ThunkAction<Promise<void>, ICryptoState, {}, TCryptoAction> => {
  const request = (): TCryptoAction => ({
    type: EncryptionActionTypes.SET_GLOBAL_SUPPORT_PUBLIC_KEY,
  });
  const fail = (error: TAppError): TCryptoAction => ({
    type: EncryptionActionTypes.SET_GLOBAL_SUPPORT_PUBLIC_KEY_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/api-basics/encryption.html#working-with-keys',
  });
  const success = (globalSupportPublicKey: string): TCryptoAction => ({
    type: EncryptionActionTypes.SET_GLOBAL_SUPPORT_PUBLIC_KEY_SUCCESS,
    globalSupportPublicKey,
  });

  return async (dispatch: ThunkDispatch<ICryptoState, {}, TCryptoAction>): Promise<void> => {
    dispatch(request());

    try {
      const { globalSupportPublicKey } = await KeyManagementService.setGlobalSupportPublicKey(globalSupportUser);

      dispatch(success(globalSupportPublicKey));
    } catch (error) {
      console.error(error);
      dispatch(fail(error));
    }
  };
};

export const fetchSupportUserPairKey = (
  passphrase: string,
): ThunkAction<Promise<void>, ICryptoState, {}, TCryptoAction> => {
  const request = (): TCryptoAction => ({
    type: EncryptionActionTypes.FETCH_SUPPORT_USER_ENCRYPTED_KEY,
  });
  const fail = (error: TAppError): TCryptoAction => ({
    type: EncryptionActionTypes.FETCH_SUPPORT_USER_ENCRYPTED_KEY_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/api-basics/encryption.html#working-with-keys',
  });
  const success = (supportUserPairKey: IPairKey): TCryptoAction => ({
    type: EncryptionActionTypes.FETCH_SUPPORT_USER_ENCRYPTED_KEY_SUCCESS,
    shouldSkipToaster: true,
    supportUserPairKey,
  });

  return async (dispatch: ThunkDispatch<ICryptoState, {}, TCryptoAction>): Promise<void> => {
    dispatch(request());

    try {
      const supportUserKeys = await KeyManagementService.getSupportUserKey(passphrase);

      if (supportUserKeys) {
        routeStore.supportUserPassphrase = passphrase;
        dispatch(success(supportUserKeys));
      } else {
        dispatch(fail('Dashboard user keys not found'));
      }
    } catch (error) {
      console.error(error);
      dispatch(fail(error));
    }
  };
};

export const fetchUserPairKey = (userId?: number): ThunkAction<Promise<void>, ICryptoState, {}, TCryptoAction> => {
  const request = (): TCryptoAction => ({
    type: EncryptionActionTypes.FETCH_SUPPORT_USER_ENCRYPTED_KEY,
  });
  const fail = (error: TAppError): TCryptoAction => ({
    type: EncryptionActionTypes.FETCH_USER_DECRYPTED_KEY_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/api-basics/encryption.html#working-with-keys',
  });
  const success = (userId: number, userPairKey: IPairKey): TCryptoAction => ({
    type: EncryptionActionTypes.FETCH_USER_DECRYPTED_KEY_SUCCESS,
    userId,
    userPairKey,
    shouldSkipToaster: true,
  });

  return async (dispatch: ThunkDispatch<ICryptoState, {}, TCryptoAction>): Promise<void> => {
    dispatch(request());

    try {
      if (!userId) return Promise.reject('User id missing to decrypt data');

      const encryptionService = await EncryptionService.getInstance();

      const userKeys = await encryptionService.decryptEndUserPrivateKey(userId);

      dispatch(success(userId, userKeys));
    } catch (error) {
      console.error(error);
      dispatch(fail(error));
    }
  };
};
